import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
  useReducer,
  Dispatch,
  useEffect,
} from 'react';
import { useAcademicsLibraryHook } from '../hooks/useAcademicsLibraryHook';
import {
  IAcademicsLibraryResponse,
  IAcademicsLibraryItem,
  LibraryAssetType,
  BorrowingStatus,
  BorrowerType,
  DamageSeverity,
  DamageStatus,
  IBorrowingRecord,
  IDamageRecord,
} from '../../../../apis/academics/academicsLibrary.interfaces';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { UseQueryResult } from '@tanstack/react-query';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';

type TQueryError = IFHError<{ message: string }>;

export enum ACTION_TYPE {
  SET_IS_MANAGE_LIBRARY_ITEM_OPEN = 'SET_IS_MANAGE_LIBRARY_ITEM_OPEN',
  SET_SELECTED_LIBRARY_ITEM = 'SET_SELECTED_LIBRARY_ITEM',
  SET_SELECTED_LIBRARY_ITEM_ID = 'SET_SELECTED_LIBRARY_ITEM_ID',
  SET_IS_CREATE_MODE = 'SET_IS_CREATE_MODE',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
  SET_IS_LIBRARY_ITEM_LOADING = 'SET_IS_LIBRARY_ITEM_LOADING',
  SET_IS_LIBRARY_ITEM_ERROR = 'SET_IS_LIBRARY_ITEM_ERROR',
  SET_LIBRARY_ITEM_ERROR_MESSAGE = 'SET_LIBRARY_ITEM_ERROR_MESSAGE',
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  UPDATE_LIBRARY_ITEMS = 'UPDATE_LIBRARY_ITEMS',
  UPDATE_ITEMS_LIST = 'UPDATE_ITEMS_LIST',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_IS_MANAGE_BORROWING_RECORD_OPEN = 'SET_IS_MANAGE_BORROWING_RECORD_OPEN',
  SET_SELECTED_BORROWING_RECORD = 'SET_SELECTED_BORROWING_RECORD',
  SET_SELECTED_BORROWING_RECORD_ID = 'SET_SELECTED_BORROWING_RECORD_ID',
  SET_SELECTED_LIBRARY_ITEM_ID_FOR_BORROWING = 'SET_SELECTED_LIBRARY_ITEM_ID_FOR_BORROWING',
  SET_IS_CREATE_BORROWING_RECORD_MODE = 'SET_IS_CREATE_BORROWING_RECORD_MODE',
  SET_IS_EDIT_BORROWING_RECORD_MODE = 'SET_IS_EDIT_BORROWING_RECORD_MODE',
  SET_IS_BORROWING_RECORD_LOADING = 'SET_IS_BORROWING_RECORD_LOADING',
  SET_IS_BORROWING_RECORD_ERROR = 'SET_IS_BORROWING_RECORD_ERROR',
  SET_BORROWING_RECORD_ERROR_MESSAGE = 'SET_BORROWING_RECORD_ERROR_MESSAGE',
}

type TAcademicsLibraryAppState = {
  isManageLibraryItemOpen: boolean;
  isLibraryItemLoading: boolean;
  isLibraryItemError: boolean;
  libraryItemErrorMessage: string;
  selectedLibraryItem: IAcademicsLibraryItem | null;
  selectedLibraryItemId: string | null;
  isCreateMode: boolean;
  isEditMode: boolean;
  itemsList: IAcademicsLibraryItem[];
  errorMessage?: string;
  isLoading: boolean;
  userParams: TPaginationParams;
  isManageBorrowingRecordOpen: boolean;
  isBorrowingRecordLoading: boolean;
  isBorrowingRecordError: boolean;
  borrowingRecordErrorMessage: string;
  selectedBorrowingRecord: IBorrowingRecord | null;
  selectedBorrowingRecordId: string | null;
  selectedLibraryItemIdForBorrowing: string | null;
  isCreateBorrowingRecordMode: boolean;
  isEditBorrowingRecordMode: boolean;
};

type Action =
  | { type: ACTION_TYPE.SET_IS_MANAGE_LIBRARY_ITEM_OPEN; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_LIBRARY_ITEM_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_LIBRARY_ITEM_ERROR; payload: boolean }
  | { type: ACTION_TYPE.SET_LIBRARY_ITEM_ERROR_MESSAGE; payload: string }
  | {
      type: ACTION_TYPE.SET_SELECTED_LIBRARY_ITEM;
      payload: IAcademicsLibraryItem | null;
    }
  | { type: ACTION_TYPE.SET_SELECTED_LIBRARY_ITEM_ID; payload: string | null }
  | { type: ACTION_TYPE.SET_IS_CREATE_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EDIT_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | {
      type: ACTION_TYPE.UPDATE_LIBRARY_ITEMS;
      payload: IAcademicsLibraryItem[];
    }
  | { type: ACTION_TYPE.UPDATE_ITEMS_LIST; payload: IAcademicsLibraryItem[] }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | { type: ACTION_TYPE.SET_IS_MANAGE_BORROWING_RECORD_OPEN; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_BORROWING_RECORD_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_BORROWING_RECORD_ERROR; payload: boolean }
  | { type: ACTION_TYPE.SET_BORROWING_RECORD_ERROR_MESSAGE; payload: string }
  | {
      type: ACTION_TYPE.SET_SELECTED_BORROWING_RECORD;
      payload: IBorrowingRecord | null;
    }
  | {
      type: ACTION_TYPE.SET_SELECTED_BORROWING_RECORD_ID;
      payload: string | null;
    }
  | {
      type: ACTION_TYPE.SET_SELECTED_LIBRARY_ITEM_ID_FOR_BORROWING;
      payload: string | null;
    }
  | { type: ACTION_TYPE.SET_IS_CREATE_BORROWING_RECORD_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EDIT_BORROWING_RECORD_MODE; payload: boolean };

const initialAcademicsLibraryState: TAcademicsLibraryAppState = {
  isManageLibraryItemOpen: false,
  isLibraryItemLoading: false,
  isLibraryItemError: false,
  libraryItemErrorMessage: '',
  selectedLibraryItem: null,
  selectedLibraryItemId: null,
  isCreateMode: false,
  isEditMode: false,
  itemsList: [],
  errorMessage: undefined,
  isLoading: false,
  userParams: {
    page: 1,
    limit: 10,
  },
  isManageBorrowingRecordOpen: false,
  isBorrowingRecordLoading: false,
  isBorrowingRecordError: false,
  borrowingRecordErrorMessage: '',
  selectedBorrowingRecord: null,
  selectedBorrowingRecordId: null,
  selectedLibraryItemIdForBorrowing: null,
  isCreateBorrowingRecordMode: false,
  isEditBorrowingRecordMode: false,
};

const academicsLibraryReducer = (
  state: TAcademicsLibraryAppState,
  action: Action
): TAcademicsLibraryAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...state,
        userParams: { ...state.userParams, ...action.payload },
      };
    case ACTION_TYPE.SET_IS_MANAGE_LIBRARY_ITEM_OPEN:
      return { ...state, isManageLibraryItemOpen: action.payload };
    case ACTION_TYPE.SET_IS_LIBRARY_ITEM_LOADING:
      return { ...state, isLibraryItemLoading: action.payload };
    case ACTION_TYPE.SET_IS_LIBRARY_ITEM_ERROR:
      return { ...state, isLibraryItemError: action.payload };
    case ACTION_TYPE.SET_LIBRARY_ITEM_ERROR_MESSAGE:
      return { ...state, libraryItemErrorMessage: action.payload };
    case ACTION_TYPE.SET_SELECTED_LIBRARY_ITEM:
      return { ...state, selectedLibraryItem: action.payload };
    case ACTION_TYPE.SET_SELECTED_LIBRARY_ITEM_ID:
      return { ...state, selectedLibraryItemId: action.payload };
    case ACTION_TYPE.SET_IS_CREATE_MODE:
      return { ...state, isCreateMode: action.payload };
    case ACTION_TYPE.SET_IS_EDIT_MODE:
      return { ...state, isEditMode: action.payload };
    case ACTION_TYPE.UPDATE_LIBRARY_ITEMS:
      return { ...state, itemsList: action.payload };
    case ACTION_TYPE.UPDATE_ITEMS_LIST:
      return { ...state, itemsList: action.payload };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_IS_MANAGE_BORROWING_RECORD_OPEN:
      return { ...state, isManageBorrowingRecordOpen: action.payload };
    case ACTION_TYPE.SET_IS_BORROWING_RECORD_LOADING:
      return { ...state, isBorrowingRecordLoading: action.payload };
    case ACTION_TYPE.SET_IS_BORROWING_RECORD_ERROR:
      return { ...state, isBorrowingRecordError: action.payload };
    case ACTION_TYPE.SET_BORROWING_RECORD_ERROR_MESSAGE:
      return { ...state, borrowingRecordErrorMessage: action.payload };
    case ACTION_TYPE.SET_SELECTED_BORROWING_RECORD:
      return { ...state, selectedBorrowingRecord: action.payload };
    case ACTION_TYPE.SET_SELECTED_BORROWING_RECORD_ID:
      return { ...state, selectedBorrowingRecordId: action.payload };
    case ACTION_TYPE.SET_SELECTED_LIBRARY_ITEM_ID_FOR_BORROWING:
      return { ...state, selectedLibraryItemIdForBorrowing: action.payload };
    case ACTION_TYPE.SET_IS_CREATE_BORROWING_RECORD_MODE:
      return { ...state, isCreateBorrowingRecordMode: action.payload };
    case ACTION_TYPE.SET_IS_EDIT_BORROWING_RECORD_MODE:
      return { ...state, isEditBorrowingRecordMode: action.payload };
    default:
      return state;
  }
};

interface TAcademicsLibraryState {
  // Queries
  libraryQuery: UseQueryResult<IAcademicsLibraryResponse, TQueryError>;

  // Refetch functions
  refetchLibrary: () => void;

  // Library item utility functions
  getItemById: (id: string) => IAcademicsLibraryItem | undefined;
  getItemByAssetCode: (assetCode: string) => IAcademicsLibraryItem | undefined;
  getItemsByAssetType: (assetType: LibraryAssetType) => IAcademicsLibraryItem[];
  getItemsByStatus: (status: BorrowingStatus) => IAcademicsLibraryItem[];
  getItemsByLocation: (location: string) => IAcademicsLibraryItem[];
  getAvailableItems: () => IAcademicsLibraryItem[];
  getBorrowedItems: () => IAcademicsLibraryItem[];
  getOverdueItems: () => IAcademicsLibraryItem[];
  getItemsByBorrower: (
    borrowerId: string,
    borrowerType?: BorrowerType
  ) => IAcademicsLibraryItem[];
  getItemsByBorrowerType: (
    borrowerType: BorrowerType
  ) => IAcademicsLibraryItem[];
  getItemsBySearchTerm: (searchTerm: string) => IAcademicsLibraryItem[];
  getItemsByTag: (tag: string) => IAcademicsLibraryItem[];
  getItemsBySubject: (subject: string) => IAcademicsLibraryItem[];
  getItemsByGradeLevel: (gradeLevel: string) => IAcademicsLibraryItem[];

  // Borrowing management functions
  getBorrowingHistory: (assetId: string) => IBorrowingRecord[];
  getCurrentBorrowings: () => IBorrowingRecord[];
  getOverdueBorrowings: () => IBorrowingRecord[];
  getBorrowingsByBorrower: (borrowerId: string) => IBorrowingRecord[];
  getBorrowingsByDateRange: (
    startDate: Date,
    endDate: Date
  ) => IBorrowingRecord[];

  // Damage management functions
  getDamageHistory: (assetId: string) => IDamageRecord[];
  getActiveDamages: () => IDamageRecord[];
  getDamagesBySeverity: (severity: DamageSeverity) => IDamageRecord[];
  getDamagesByStatus: (status: DamageStatus) => IDamageRecord[];
  getItemsNeedingRepair: () => IAcademicsLibraryItem[];
  getItemsNeedingReplacement: () => IAcademicsLibraryItem[];

  // Statistics and analytics functions
  getTotalLibraryValue: () => number;
  getAssetTypeSummary: () => {
    assetType: LibraryAssetType;
    totalCount: number;
    availableCount: number;
    borrowedCount: number;
  }[];
  getStatusSummary: () => {
    status: BorrowingStatus;
    totalCount: number;
  }[];
  getLocationSummary: () => {
    location: string;
    totalCount: number;
    availableCount: number;
  }[];
  getBorrowingStatistics: () => {
    totalBorrowings: number;
    activeBorrowings: number;
    overdueBorrowings: number;
    totalFines: number;
    unpaidFines: number;
  };
  getDamageStatistics: () => {
    totalDamages: number;
    activeDamages: number;
    totalRepairCost: number;
    totalReplacementCost: number;
  };

  // Loading and error states
  isLoading: boolean;
  isError: boolean;
  error: TQueryError | null;
}

type TAcademicsLibraryContextState = {
  academicsLibraryAppState: TAcademicsLibraryAppState;
  academicsLibraryRecords: IAcademicsLibraryItem[];
};

interface AcademicsLibraryProviderProps {
  children: ReactNode;
  paginationParams?: TPaginationParams;
}

const AcademicsLibraryContext = createContext<
  TAcademicsLibraryState | undefined
>(undefined);

const AcademicsLibraryAppStateContext = createContext<
  TAcademicsLibraryContextState | undefined
>(undefined);

const AcademicsLibraryDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

export const AcademicsLibraryProvider: React.FC<
  AcademicsLibraryProviderProps
> = ({ children, paginationParams = { page: 1, limit: 10 } }) => {
  const libraryQuery = useAcademicsLibraryHook(paginationParams);
  const [appState, dispatch] = useReducer(academicsLibraryReducer, {
    ...initialAcademicsLibraryState,
    userParams: paginationParams,
    itemsList: libraryQuery.data?.items || [],
  });

  const refetchLibrary = useCallback(() => {
    libraryQuery.refetch();
  }, [libraryQuery]);

  // Library item utility functions
  const getItemById = useCallback(
    (id: string): IAcademicsLibraryItem | undefined => {
      return libraryQuery.data?.items.find((item) => item.id === id);
    },
    [libraryQuery]
  );

  const getItemByAssetCode = useCallback(
    (assetCode: string): IAcademicsLibraryItem | undefined => {
      return libraryQuery.data?.items.find(
        (item) => item.assetCode === assetCode
      );
    },
    [libraryQuery]
  );

  const getItemsByAssetType = useCallback(
    (assetType: LibraryAssetType): IAcademicsLibraryItem[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items.filter(
        (item) => item.assetType === assetType
      );
    },
    [libraryQuery]
  );

  const getItemsByStatus = useCallback(
    (status: BorrowingStatus): IAcademicsLibraryItem[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items.filter(
        (item) => item.currentStatus === status
      );
    },
    [libraryQuery]
  );

  const getItemsByLocation = useCallback(
    (location: string): IAcademicsLibraryItem[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items.filter(
        (item) => item.location === location
      );
    },
    [libraryQuery]
  );

  const getAvailableItems = useCallback((): IAcademicsLibraryItem[] => {
    if (!libraryQuery.data) return [];
    return libraryQuery.data.items.filter(
      (item) =>
        item.isAvailable &&
        item.canBeBorrowed &&
        item.currentStatus === BorrowingStatus.AVAILABLE
    );
  }, [libraryQuery]);

  const getBorrowedItems = useCallback((): IAcademicsLibraryItem[] => {
    if (!libraryQuery.data) return [];
    return libraryQuery.data.items.filter(
      (item) => item.currentStatus === BorrowingStatus.BORROWED
    );
  }, [libraryQuery]);

  const getOverdueItems = useCallback((): IAcademicsLibraryItem[] => {
    if (!libraryQuery.data) return [];
    return libraryQuery.data.items.filter(
      (item) => item.currentStatus === BorrowingStatus.OVERDUE
    );
  }, [libraryQuery]);

  const getItemsByBorrower = useCallback(
    (
      borrowerId: string,
      borrowerType?: BorrowerType
    ): IAcademicsLibraryItem[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items.filter((item) => {
        if (!item.currentBorrowing) return false;
        const matchesBorrower = item.currentBorrowing.borrowerId === borrowerId;
        const matchesType =
          !borrowerType || item.currentBorrowing.borrowerType === borrowerType;
        return matchesBorrower && matchesType;
      });
    },
    [libraryQuery]
  );

  const getItemsByBorrowerType = useCallback(
    (borrowerType: BorrowerType): IAcademicsLibraryItem[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items.filter(
        (item) =>
          item.currentBorrowing?.borrowerType === borrowerType &&
          item.currentStatus === BorrowingStatus.BORROWED
      );
    },
    [libraryQuery]
  );

  const getItemsBySearchTerm = useCallback(
    (searchTerm: string): IAcademicsLibraryItem[] => {
      if (!libraryQuery.data) return [];
      const term = searchTerm.toLowerCase();
      return libraryQuery.data.items.filter(
        (item) =>
          item.title.toLowerCase().includes(term) ||
          item.assetCode.toLowerCase().includes(term) ||
          item.description?.toLowerCase().includes(term) ||
          item.author?.toLowerCase().includes(term) ||
          item.isbn?.toLowerCase().includes(term) ||
          item.tags.some((tag) => tag.toLowerCase().includes(term))
      );
    },
    [libraryQuery]
  );

  const getItemsByTag = useCallback(
    (tag: string): IAcademicsLibraryItem[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items.filter((item) => item.tags.includes(tag));
    },
    [libraryQuery]
  );

  const getItemsBySubject = useCallback(
    (subject: string): IAcademicsLibraryItem[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items.filter(
        (item) => item.subject?.toLowerCase() === subject.toLowerCase()
      );
    },
    [libraryQuery]
  );

  const getItemsByGradeLevel = useCallback(
    (gradeLevel: string): IAcademicsLibraryItem[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items.filter(
        (item) => item.gradeLevel === gradeLevel
      );
    },
    [libraryQuery]
  );

  // Borrowing management functions
  const getBorrowingHistory = useCallback(
    (assetId: string): IBorrowingRecord[] => {
      if (!libraryQuery.data) return [];
      const item = libraryQuery.data.items.find((item) => item.id === assetId);
      return item?.borrowingHistory || [];
    },
    [libraryQuery]
  );

  const getCurrentBorrowings = useCallback((): IBorrowingRecord[] => {
    if (!libraryQuery.data) return [];
    return libraryQuery.data.items
      .filter((item) => item.currentBorrowing)
      .map((item) => item.currentBorrowing!)
      .filter((borrowing) => borrowing.status === BorrowingStatus.BORROWED);
  }, [libraryQuery]);

  const getOverdueBorrowings = useCallback((): IBorrowingRecord[] => {
    if (!libraryQuery.data) return [];
    const now = new Date();
    return libraryQuery.data.items
      .filter((item) => item.currentBorrowing)
      .map((item) => item.currentBorrowing!)
      .filter(
        (borrowing) =>
          borrowing.status === BorrowingStatus.OVERDUE ||
          (borrowing.dueDate && new Date(borrowing.dueDate) < now)
      );
  }, [libraryQuery]);

  const getBorrowingsByBorrower = useCallback(
    (borrowerId: string): IBorrowingRecord[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items
        .flatMap((item) => item.borrowingHistory)
        .filter((record) => record.borrowerId === borrowerId);
    },
    [libraryQuery]
  );

  const getBorrowingsByDateRange = useCallback(
    (startDate: Date, endDate: Date): IBorrowingRecord[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items
        .flatMap((item) => item.borrowingHistory)
        .filter((record) => {
          const borrowedDate = new Date(record.borrowedDate);
          return borrowedDate >= startDate && borrowedDate <= endDate;
        });
    },
    [libraryQuery]
  );

  // Damage management functions
  const getDamageHistory = useCallback(
    (assetId: string): IDamageRecord[] => {
      if (!libraryQuery.data) return [];
      const item = libraryQuery.data.items.find((item) => item.id === assetId);
      return item?.damageHistory || [];
    },
    [libraryQuery]
  );

  const getActiveDamages = useCallback((): IDamageRecord[] => {
    if (!libraryQuery.data) return [];
    return libraryQuery.data.items
      .flatMap((item) => item.damageHistory)
      .filter(
        (damage) =>
          damage.damageStatus !== DamageStatus.REPAIRED &&
          damage.damageStatus !== DamageStatus.WRITTEN_OFF
      );
  }, [libraryQuery]);

  const getDamagesBySeverity = useCallback(
    (severity: DamageSeverity): IDamageRecord[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items
        .flatMap((item) => item.damageHistory)
        .filter((damage) => damage.damageSeverity === severity);
    },
    [libraryQuery]
  );

  const getDamagesByStatus = useCallback(
    (status: DamageStatus): IDamageRecord[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items
        .flatMap((item) => item.damageHistory)
        .filter((damage) => damage.damageStatus === status);
    },
    [libraryQuery]
  );

  const getItemsNeedingRepair = useCallback((): IAcademicsLibraryItem[] => {
    if (!libraryQuery.data) return [];
    return libraryQuery.data.items.filter((item) =>
      item.damageHistory.some(
        (damage) =>
          damage.damageStatus === DamageStatus.REPORTED ||
          damage.damageStatus === DamageStatus.UNDER_REVIEW ||
          damage.damageStatus === DamageStatus.REPAIRING
      )
    );
  }, [libraryQuery]);

  const getItemsNeedingReplacement =
    useCallback((): IAcademicsLibraryItem[] => {
      if (!libraryQuery.data) return [];
      return libraryQuery.data.items.filter((item) =>
        item.damageHistory.some(
          (damage) => damage.damageStatus === DamageStatus.REPLACEMENT_NEEDED
        )
      );
    }, [libraryQuery]);

  // Statistics and analytics functions
  const getTotalLibraryValue = useCallback((): number => {
    if (!libraryQuery.data) return 0;
    return libraryQuery.data.items.reduce(
      (total, item) => total + (item.purchaseCost || 0),
      0
    );
  }, [libraryQuery]);

  const getAssetTypeSummary = useCallback((): {
    assetType: LibraryAssetType;
    totalCount: number;
    availableCount: number;
    borrowedCount: number;
  }[] => {
    if (!libraryQuery.data) return [];
    const summary = new Map<
      LibraryAssetType,
      { totalCount: number; availableCount: number; borrowedCount: number }
    >();

    libraryQuery.data.items.forEach((item) => {
      const current = summary.get(item.assetType) || {
        totalCount: 0,
        availableCount: 0,
        borrowedCount: 0,
      };
      summary.set(item.assetType, {
        totalCount: current.totalCount + 1,
        availableCount:
          current.availableCount +
          (item.currentStatus === BorrowingStatus.AVAILABLE ? 1 : 0),
        borrowedCount:
          current.borrowedCount +
          (item.currentStatus === BorrowingStatus.BORROWED ? 1 : 0),
      });
    });

    return Array.from(summary.entries()).map(([assetType, data]) => ({
      assetType,
      ...data,
    }));
  }, [libraryQuery]);

  const getStatusSummary = useCallback((): {
    status: BorrowingStatus;
    totalCount: number;
  }[] => {
    if (!libraryQuery.data) return [];
    const summary = new Map<BorrowingStatus, number>();

    libraryQuery.data.items.forEach((item) => {
      const current = summary.get(item.currentStatus) || 0;
      summary.set(item.currentStatus, current + 1);
    });

    return Array.from(summary.entries()).map(([status, totalCount]) => ({
      status,
      totalCount,
    }));
  }, [libraryQuery]);

  const getLocationSummary = useCallback((): {
    location: string;
    totalCount: number;
    availableCount: number;
  }[] => {
    if (!libraryQuery.data) return [];
    const summary = new Map<
      string,
      { totalCount: number; availableCount: number }
    >();

    libraryQuery.data.items.forEach((item) => {
      const current = summary.get(item.location) || {
        totalCount: 0,
        availableCount: 0,
      };
      summary.set(item.location, {
        totalCount: current.totalCount + 1,
        availableCount:
          current.availableCount +
          (item.currentStatus === BorrowingStatus.AVAILABLE ? 1 : 0),
      });
    });

    return Array.from(summary.entries()).map(([location, data]) => ({
      location,
      ...data,
    }));
  }, [libraryQuery]);

  const getBorrowingStatistics = useCallback((): {
    totalBorrowings: number;
    activeBorrowings: number;
    overdueBorrowings: number;
    totalFines: number;
    unpaidFines: number;
  } => {
    if (!libraryQuery.data) {
      return {
        totalBorrowings: 0,
        activeBorrowings: 0,
        overdueBorrowings: 0,
        totalFines: 0,
        unpaidFines: 0,
      };
    }

    const allBorrowings = libraryQuery.data.items.flatMap(
      (item) => item.borrowingHistory
    );
    const activeBorrowings = libraryQuery.data.items.filter(
      (item) => item.currentStatus === BorrowingStatus.BORROWED
    ).length;
    const overdueBorrowings = libraryQuery.data.items.filter(
      (item) => item.currentStatus === BorrowingStatus.OVERDUE
    ).length;

    const totalFines = allBorrowings.reduce(
      (sum, record) => sum + (record.fineAmount || 0),
      0
    );
    const unpaidFines = allBorrowings.reduce(
      (sum, record) =>
        sum + (record.fineAmount && !record.finePaid ? record.fineAmount : 0),
      0
    );

    return {
      totalBorrowings: allBorrowings.length,
      activeBorrowings,
      overdueBorrowings,
      totalFines,
      unpaidFines,
    };
  }, [libraryQuery]);

  const getDamageStatistics = useCallback((): {
    totalDamages: number;
    activeDamages: number;
    totalRepairCost: number;
    totalReplacementCost: number;
  } => {
    if (!libraryQuery.data) {
      return {
        totalDamages: 0,
        activeDamages: 0,
        totalRepairCost: 0,
        totalReplacementCost: 0,
      };
    }

    const allDamages = libraryQuery.data.items.flatMap(
      (item) => item.damageHistory
    );
    const activeDamages = allDamages.filter(
      (damage) =>
        damage.damageStatus !== DamageStatus.REPAIRED &&
        damage.damageStatus !== DamageStatus.WRITTEN_OFF
    ).length;

    const totalRepairCost = allDamages.reduce(
      (sum, damage) => sum + (damage.actualRepairCost || 0),
      0
    );
    const totalReplacementCost = libraryQuery.data.items
      .filter((item) =>
        item.damageHistory.some(
          (damage) => damage.damageStatus === DamageStatus.REPLACEMENT_NEEDED
        )
      )
      .reduce((sum, item) => sum + (item.replacementCost || 0), 0);

    return {
      totalDamages: allDamages.length,
      activeDamages,
      totalRepairCost,
      totalReplacementCost,
    };
  }, [libraryQuery]);

  // Loading and error states
  const isLoading = libraryQuery.isLoading;
  const isError = libraryQuery.isError;
  const error = libraryQuery.error || null;

  // Sync itemsList with query data
  useEffect(() => {
    if (libraryQuery.data?.items) {
      dispatch({
        type: ACTION_TYPE.UPDATE_ITEMS_LIST,
        payload: libraryQuery.data.items,
      });
    }
  }, [libraryQuery.data?.items]);

  const value = useMemo(
    () => ({
      // Queries
      libraryQuery,

      // Refetch functions
      refetchLibrary,

      // Library item utility functions
      getItemById,
      getItemByAssetCode,
      getItemsByAssetType,
      getItemsByStatus,
      getItemsByLocation,
      getAvailableItems,
      getBorrowedItems,
      getOverdueItems,
      getItemsByBorrower,
      getItemsByBorrowerType,
      getItemsBySearchTerm,
      getItemsByTag,
      getItemsBySubject,
      getItemsByGradeLevel,

      // Borrowing management functions
      getBorrowingHistory,
      getCurrentBorrowings,
      getOverdueBorrowings,
      getBorrowingsByBorrower,
      getBorrowingsByDateRange,

      // Damage management functions
      getDamageHistory,
      getActiveDamages,
      getDamagesBySeverity,
      getDamagesByStatus,
      getItemsNeedingRepair,
      getItemsNeedingReplacement,

      // Statistics and analytics functions
      getTotalLibraryValue,
      getAssetTypeSummary,
      getStatusSummary,
      getLocationSummary,
      getBorrowingStatistics,
      getDamageStatistics,

      // Loading and error states
      isLoading,
      isError,
      error,
    }),
    [
      libraryQuery,
      refetchLibrary,
      getItemById,
      getItemByAssetCode,
      getItemsByAssetType,
      getItemsByStatus,
      getItemsByLocation,
      getAvailableItems,
      getBorrowedItems,
      getOverdueItems,
      getItemsByBorrower,
      getItemsByBorrowerType,
      getItemsBySearchTerm,
      getItemsByTag,
      getItemsBySubject,
      getItemsByGradeLevel,
      getBorrowingHistory,
      getCurrentBorrowings,
      getOverdueBorrowings,
      getBorrowingsByBorrower,
      getBorrowingsByDateRange,
      getDamageHistory,
      getActiveDamages,
      getDamagesBySeverity,
      getDamagesByStatus,
      getItemsNeedingRepair,
      getItemsNeedingReplacement,
      getTotalLibraryValue,
      getAssetTypeSummary,
      getStatusSummary,
      getLocationSummary,
      getBorrowingStatistics,
      getDamageStatistics,
      isLoading,
      isError,
      error,
    ]
  );

  const appStateValue: TAcademicsLibraryContextState = useMemo(
    () => ({
      academicsLibraryAppState: appState,
      academicsLibraryRecords: appState.itemsList,
    }),
    [appState]
  );

  return (
    <AcademicsLibraryContext.Provider value={value}>
      <AcademicsLibraryAppStateContext.Provider value={appStateValue}>
        <AcademicsLibraryDispatchContext.Provider value={dispatch}>
          {children}
        </AcademicsLibraryDispatchContext.Provider>
      </AcademicsLibraryAppStateContext.Provider>
    </AcademicsLibraryContext.Provider>
  );
};

export const useAcademicsLibraryContext = (): TAcademicsLibraryState => {
  const context = useContext(AcademicsLibraryContext);

  if (context === undefined) {
    throw new Error(
      'useAcademicsLibraryContext must be used within a AcademicsLibraryProvider'
    );
  }

  return context;
};

export const useAcademicsLibraryState = (): TAcademicsLibraryContextState => {
  const context = useContext(AcademicsLibraryAppStateContext);
  if (context === undefined) {
    throw new Error(
      'useAcademicsLibraryState must be used within a AcademicsLibraryProvider'
    );
  }
  return context;
};

export const useAcademicsLibraryDispatch = (): Dispatch<Action> => {
  const context = useContext(AcademicsLibraryDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useAcademicsLibraryDispatch must be used within a AcademicsLibraryProvider'
    );
  }
  return context;
};

export const useAcademicsLibraryActions = () => {
  const dispatch = useAcademicsLibraryDispatch();
  if (dispatch === undefined) {
    throw new Error(
      'useAcademicsLibraryActions must be used within a AcademicsLibraryProvider'
    );
  }

  const setIsManageLibraryItemOpen = (isOpen: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_MANAGE_LIBRARY_ITEM_OPEN,
      payload: isOpen,
    });
  };

  const setSelectedLibraryItem = (item: IAcademicsLibraryItem | null) => {
    dispatch?.({ type: ACTION_TYPE.SET_SELECTED_LIBRARY_ITEM, payload: item });
  };

  const setIsLibraryItemLoading = (isLoading: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_LIBRARY_ITEM_LOADING,
      payload: isLoading,
    });
  };

  const setIsLibraryItemError = (isError: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_LIBRARY_ITEM_ERROR,
      payload: isError,
    });
  };

  const setLibraryItemErrorMessage = (errorMessage: string) => {
    dispatch?.({
      type: ACTION_TYPE.SET_LIBRARY_ITEM_ERROR_MESSAGE,
      payload: errorMessage,
    });
  };

  const setSelectedLibraryItemId = (itemId: string | null) => {
    dispatch?.({
      type: ACTION_TYPE.SET_SELECTED_LIBRARY_ITEM_ID,
      payload: itemId,
    });
  };

  const setIsCreateMode = (isCreateMode: boolean) => {
    dispatch?.({ type: ACTION_TYPE.SET_IS_CREATE_MODE, payload: isCreateMode });
  };

  const setIsEditMode = (isEditMode: boolean) => {
    dispatch?.({ type: ACTION_TYPE.SET_IS_EDIT_MODE, payload: isEditMode });
  };

  const setIsManageBorrowingRecordOpen = (isOpen: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_MANAGE_BORROWING_RECORD_OPEN,
      payload: isOpen,
    });
  };

  const setSelectedBorrowingRecord = (record: IBorrowingRecord | null) => {
    dispatch?.({
      type: ACTION_TYPE.SET_SELECTED_BORROWING_RECORD,
      payload: record,
    });
  };

  const setIsBorrowingRecordLoading = (isLoading: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_BORROWING_RECORD_LOADING,
      payload: isLoading,
    });
  };

  const setIsBorrowingRecordError = (isError: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_BORROWING_RECORD_ERROR,
      payload: isError,
    });
  };

  const setBorrowingRecordErrorMessage = (errorMessage: string) => {
    dispatch?.({
      type: ACTION_TYPE.SET_BORROWING_RECORD_ERROR_MESSAGE,
      payload: errorMessage,
    });
  };

  const setSelectedBorrowingRecordId = (recordId: string | null) => {
    dispatch?.({
      type: ACTION_TYPE.SET_SELECTED_BORROWING_RECORD_ID,
      payload: recordId,
    });
  };

  const setSelectedLibraryItemIdForBorrowing = (itemId: string | null) => {
    dispatch?.({
      type: ACTION_TYPE.SET_SELECTED_LIBRARY_ITEM_ID_FOR_BORROWING,
      payload: itemId,
    });
  };

  const setIsCreateBorrowingRecordMode = (isCreateMode: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_CREATE_BORROWING_RECORD_MODE,
      payload: isCreateMode,
    });
  };

  const setIsEditBorrowingRecordMode = (isEditMode: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_EDIT_BORROWING_RECORD_MODE,
      payload: isEditMode,
    });
  };

  return {
    setIsManageLibraryItemOpen,
    setSelectedLibraryItem,
    setIsLibraryItemLoading,
    setIsLibraryItemError,
    setLibraryItemErrorMessage,
    setSelectedLibraryItemId,
    setIsCreateMode,
    setIsEditMode,
    setIsManageBorrowingRecordOpen,
    setSelectedBorrowingRecord,
    setIsBorrowingRecordLoading,
    setIsBorrowingRecordError,
    setBorrowingRecordErrorMessage,
    setSelectedBorrowingRecordId,
    setSelectedLibraryItemIdForBorrowing,
    setIsCreateBorrowingRecordMode,
    setIsEditBorrowingRecordMode,
  };
};
